// Canonical registry for the contract-detail tab bar: the single source of
// truth for tab order, slugs, labels, and icons. The rendered tab bar follows
// CONTRACT_TABS order; ManagedContractDetail and the adaptive tab-bar
// components consume TAB_SLUGS / isTabSlug / getTabDef rather than holding
// their own parallel constants.
//
// Slugs are a deep-link contract — ?tab=<slug> bookmarks must keep working —
// so the set may not change without a redirect. The combined 'variations-sows'
// slug is a frontend-only UI grouping aggregating the backend 'sow' and
// 'variation' tracking types; those two never appear as standalone tabs.
//
// tabSlugForType() maps every backend TabLevelType to its UI tab slug — the
// authoritative translation layer used by buildTrackingDeepLink and the
// command palette's record URL builder.

import {
  FileText,
  ClipboardList,
  Flag,
  Users,
  BarChart3,
  TriangleAlert,
  FileSignature,
  Shield,
  DollarSign,
  FileChartColumn,
  type LucideIcon,
} from "lucide-react";
import type { TabLevelType } from "../../../types";

/** Combined tab slug: 'variations-sows', 'finance' and 'reports' are not part
 *  of TabLevelType because they are frontend-only UI groupings with no backend
 *  tracking type — 'variations-sows' aggregates the 'sow'/'variation' types,
 *  'finance' surfaces the separate purchase-order/invoice domain, and
 *  'reports' composes every domain into read-only report views (see module
 *  docstring). */
export type TabSlug =
  | "details"
  | TabLevelType
  | "variations-sows"
  | "finance"
  | "reports";

export interface ContractTabDef {
  slug: TabSlug;
  label: string;
  icon: LucideIcon;
}

/** Editorial order, top to bottom of importance for a contract manager:
 *  the contract record itself, then delivery (deliverables, milestones,
 *  meetings, KPIs), then exceptions (issues), then commercial change
 *  (variations & SOWs), then compliance (insurances), then the commercial
 *  finance domain (purchase orders & invoices). Insurances and finance sit
 *  near the tail as the least-frequently-visited surfaces, and Reports sits
 *  last: it reads from every other tab's records rather than owning any, so
 *  it is a destination reached after the underlying data is in place. */
export const CONTRACT_TABS: readonly ContractTabDef[] = [
  { slug: "details", label: "Details", icon: FileText },
  { slug: "deliverable", label: "Deliverables", icon: ClipboardList },
  { slug: "milestone", label: "Milestones", icon: Flag },
  { slug: "meeting", label: "Meetings", icon: Users },
  { slug: "kpi", label: "KPIs", icon: BarChart3 },
  { slug: "issue", label: "Issues", icon: TriangleAlert },
  { slug: "variations-sows", label: "Variations & SOWs", icon: FileSignature },
  { slug: "insurance", label: "Insurances", icon: Shield },
  { slug: "finance", label: "Finance", icon: DollarSign },
  { slug: "reports", label: "Reports", icon: FileChartColumn },
] as const;

export const TAB_SLUGS: readonly TabSlug[] = CONTRACT_TABS.map((t) => t.slug);

const TAB_DEFS_BY_SLUG = new Map<TabSlug, ContractTabDef>(
  CONTRACT_TABS.map((t) => [t.slug, t]),
);

export function isTabSlug(value: string | null): value is TabSlug {
  return !!value && TAB_DEFS_BY_SLUG.has(value as TabSlug);
}

export function getTabDef(slug: TabSlug): ContractTabDef {
  // Every TabSlug is registered, so the lookup is total for valid inputs.
  return TAB_DEFS_BY_SLUG.get(slug)!;
}

/**
 * Maps a backend TabLevelType to its UI tab slug. The 'sow' and 'variation'
 * types share the combined 'variations-sows' tab; all other types map to
 * their identical slug. Use this wherever a backend type must appear in a
 * ?tab= deep link or palette URL — never inline the type directly.
 */
export function tabSlugForType(type: TabLevelType): TabSlug {
  if (type === "sow" || type === "variation") return "variations-sows";
  return type;
}
